import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Eye } from "../../assets/svgs/eye.svg";
import { ReactComponent as Logo } from "../../assets/svgs/logo.svg";
import { API_URLS } from "../../api/apiUrls";
import { postApi } from "../../api/api_instance";
import { InputField } from "../../components/Elements/InputField/InputField";
import { Button, Checkbox } from "antd";
import CustomButton from "../../components/Elements/Buttons/CustomButtons";
import loginImage from "../../assets/images/LoginImage.png";

export const SignUp = () => {
  const navigate = useNavigate();
  const [signUpData, setSignUpData] = useState({
    name: "",
    email: "",
    password: "",
    otp: "",
    retypePassword: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [retypePasswordVisible, setRetypePasswordVisible] = useState(false);
  const [acceptAllTerms, setAcceptAllTerms] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpData({ ...signUpData, [name]: value });
  };

  const handleSendCode = async () => {
    if (signUpData.email) {
      try {
        const response = await postApi(
          {
            apiUrl: API_URLS.OTP_EMAIL,
            data: { email: signUpData.email },
          },
          false,
        );

        if (response.success) {
          toast.success("Verification code sent to your email!");
          setIsCodeSent(true);
        } else {
          toast.error(response.message || "Failed to send code.");
        }
      } catch (error) {
        toast.error("Failed to send verification code.");
      }
    } else {
      toast.error("Please enter your email first");
    }
  };

  const handleSignUp = async () => {
    if (!signUpData.name.trim()) {
      toast.error("Please enter your organisation name.");
      return;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(signUpData.email)) {
      toast.error("Please enter a valid email.");
      return;
    }

    if (signUpData.password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }

    if (signUpData.password !== signUpData.retypePassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (!signUpData.otp) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!acceptAllTerms) {
      toast.error("Please accept the terms and conditions.");
      return;
    }

    try {
      const response = await postApi(
        {
          apiUrl: API_URLS.SIGN_UP_CLIENT,
          data: signUpData,
        },
        false,
      );

      if (response.success) {
        toast.success(response.message);
        navigate("/login");
      }
    } catch (error) {
      toast.error("Sign up failed. Please try again.");
    }
  };

  const handlePasswordVisibilityToggle = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleRetypePasswordVisibilityToggle = () => {
    setRetypePasswordVisible(!retypePasswordVisible);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-white">
      {/* Left Side with Background Image - Hidden on mobile */}
      <div className="flex w-full md:w-3/5 bg-gradient-to-br from-[#06513D] to-[#06513D] text-white p-6 md:p-10 items-center justify-center">
        <div className="max-w-lg text-center space-y-6">
          <h2 className="text-4xl md:text-5xl font-bold leading-snug">
            <img src={loginImage} alt="Carburn Logo" className="h-22 md:h-35" />
          </h2>
        </div>
      </div>

      {/* Right Side SignUp Form */}
      <div className="w-full md:w-2/5 lg:w-2/5 flex flex-col justify-center px-4 lg:px-8 py-2 md:py-0">
        <div className="w-full max-w-md mx-auto">
          {/* Logo Section */}
          <div className="flex justify-center my-1">
            <Logo className="w-16 h-12 md:w-20 md:h-16" />
          </div>

          {/* Header */}
          <div className="text-center mb-3">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-[#06513D] mb-1">
              Sign Up
            </h2>
            <h3 className="text-xs md:text-sm font-light text-gray-400">
              Enter details to create your account
            </h3>
          </div>

          {/* Form Fields */}
          <div className="space-y-3 md:space-y-4">
            <div className="flex gap-0 border-2 rounded-2xl border-[#06513D] px-2">
              <InputField
                label="Organisation Name"
                name="name"
                value={signUpData.name}
                onChange={handleSignUpChange}
                type="text"
                placeholder="Enter organisation name"
              />
            </div>

            <div className="flex gap-0 border-2 rounded-2xl border-[#06513D] pl-2">
              <InputField
                label="Email Id"
                name="email"
                value={signUpData.email}
                onChange={handleSignUpChange}
                type="email"
                placeholder="Enter Email"
              />
              <Button
                onClick={handleSendCode}
                className="h-full"
                style={{
                  background: "#06513D",
                  color: "white",
                  padding: "18px",
                  borderRadius: "0px 12px 12px 0px",
                  border: "none",
                  outline: "none",
                }}
                disabled={isCodeSent}
              >
                {isCodeSent ? "Code Sent" : "Send Code"}
              </Button>
            </div>

            <div className="flex gap-0 border-2 rounded-2xl border-[#06513D] px-2">
              <InputField
                label="Verification Code"
                name="otp"
                value={signUpData.otp}
                onChange={handleSignUpChange}
                type="text"
                placeholder="Enter Verification Code"
              />
            </div>

            <div className="flex gap-0 border-2 rounded-2xl border-[#06513D] px-2">
              <InputField
                label="Password"
                name="password"
                value={signUpData.password}
                onChange={handleSignUpChange}
                type={passwordVisible ? "text" : "password"}
                placeholder="Enter Password"
                endAdornment={
                  <Eye
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "20px",
                      cursor: "pointer",
                    }}
                    onClick={handlePasswordVisibilityToggle}
                  />
                }
              />
            </div>

            <div className="flex gap-0 border-2 rounded-2xl border-[#06513D] px-2">
              <InputField
                label="Retype Password"
                name="retypePassword"
                value={signUpData.retypePassword}
                onChange={handleSignUpChange}
                placeholder="Retype Password"
                type={retypePasswordVisible ? "text" : "password"}
                endAdornment={
                  <Eye
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "20px",
                      cursor: "pointer",
                    }}
                    onClick={handleRetypePasswordVisibilityToggle}
                  />
                }
              />
            </div>
          </div>

          {/* Terms & Conditions Checkbox */}
          <div className="mt-3 mb-4">
            <Checkbox
              checked={acceptAllTerms}
              onChange={(e) => setAcceptAllTerms(e.target.checked)}
              className="text-sm"
            >
              <span className="text-[10px] md:text-[11px] font-light">
                Accept{" "}
                <a
                  href="/policies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-custom-blue hover:underline cursor-pointer"
                >
                  Term & Condition, Policies & terms of use
                </a>{" "}
                to proceed
              </span>
            </Checkbox>
          </div>

          {/* Sign Up Button */}
          <div className="mb-2">
            <CustomButton
              buttonType="button"
              variant="ok"
              buttonText="Sign Up"
              className="w-full text-sm md:text-base font-normal h-[40px] md:h-[45px]"
              onClick={handleSignUp}
            />
          </div>

          {/* Sign In Link */}
          <div className="text-center pb-4">
            <p className="text-sm md:text-base">
              Already have an account?{" "}
              <span
                onClick={() => navigate("/organization/login")}
                className="text-custom-blue cursor-pointer font-light"
              >
                Sign in
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
